/**
 * The org + location the app-wide alert bell and alarm run against.
 *
 * `GET /api/alerts/queue/` requires a `location_id` — there is no org-wide queue
 * query — but the bell lives in the top bar of every page, most of which have no
 * location of their own. This resolves one, in priority order:
 *
 *   1. the location the operator is actually looking at (`useTimezoneStore`'s
 *      `activeLocationId`, navigation state the map sets as they browse);
 *   2. the persisted alerts-workspace location, so a fresh tab that has not
 *      touched the map still watches the site the operator works;
 *   3. the first location the org exposes.
 *
 * (3) also HEALS a stale selection — a persisted location the operator lost
 * access to, or one belonging to a previous org — the same way
 * `alerts-queue-page.tsx` does. Without it the bell would sit silently on a
 * location that answers 403 forever.
 *
 * Changing the resolved location re-seeds the notify store silently (backlog, no
 * chime), which is the accepted cost of following the operator around: it is far
 * better than chiming a new location's entire pending backlog at them.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useGetAllLocations } from "@/locations/api/queries/location.queries.ts";
import { useAlertsWorkspaceStore } from "@/alerts/stores/use-alerts-workspace-store";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore.ts";
import { useTimezoneStore } from "@/shared/stores/useTimezoneStore.ts";

/**
 * How long the resolved location must hold steady before the app acts on it.
 * Long enough to absorb a page publishing its own selection a tick after mount,
 * short enough to be invisible.
 */
const SCOPE_SETTLE_MS = 750;

export interface ActiveAlertScope {
  organizationId: number | null;
  locationId: number | null;
  /** Stable key for the notify store. Deliberately carries NO date window. */
  scopeKey: string | null;
}

export const useActiveAlertScope = (): ActiveAlertScope => {
  const organizationId = useOrganizationStore((s) => s.selectedOrganizationId);
  const activeLocationId = useTimezoneStore((s) => s.activeLocationId);
  const queueLocationId = useAlertsWorkspaceStore((s) => s.queueLocationId);
  const setQueueLocation = useAlertsWorkspaceStore((s) => s.setQueueLocation);

  const locationsQuery = useGetAllLocations();
  const locations = useMemo(() => locationsQuery.data?.data ?? [], [locationsQuery.data]);

  // Heal a persisted location the operator can no longer use. Only the PERSISTED
  // one is healed: `activeLocationId` is transient navigation state owned by the
  // page the operator is on, and writing to it from here would fight that page.
  useEffect(() => {
    if (locations.length === 0) return;
    if (!locations.some((location) => location.id === queueLocationId)) {
      setQueueLocation(locations[0].id);
    }
  }, [locations, queueLocationId, setQueueLocation]);

  const resolvedLocationId = useMemo(() => {
    // Only a location the org actually exposes may be queried — an id left over
    // from another tenant would 403 on every poll.
    const known = (id: number | null): number | null =>
      id != null && locations.some((location) => location.id === id) ? id : null;

    return (
      known(activeLocationId) ??
      known(queueLocationId) ??
      (locations.length > 0 ? locations[0].id : null)
    );
  }, [activeLocationId, queueLocationId, locations]);

  /**
   * Hold the location still until it stops moving.
   *
   * On a page that owns a location — `/map` above all — resolution arrives in
   * two steps: this hook falls back to the persisted (or first) location as soon
   * as the list loads, and a moment later the page publishes the one the
   * operator is actually on. Acting on the intermediate value is not merely
   * wasteful, it BREAKS the live channel: each location gets its own
   * scope-bound Firebase custom token, and signing in with the second REPLACES
   * the auth session while the first listener is still attached — so that
   * listener is suddenly authenticated for a location it may not read, and
   * Firestore answers permission-denied. The channel then degrades to polling
   * and alerts arrive on a 20s poll instead of instantly.
   *
   * Settling first costs a beat on load and removes the race entirely. A genuine
   * location switch later is a single change and passes straight through after
   * the same short wait.
   */
  const [settledLocationId, setSettledLocationId] = useState<number | null>(null);
  const pendingRef = useRef(resolvedLocationId);
  pendingRef.current = resolvedLocationId;

  // Drop the settled location the instant the ORG changes.
  //
  // A location only means anything inside the org that exposes it, but the
  // settled value here is location-only, so it survived an org switch: the org
  // header flipped while this still held the previous org's location, and the
  // bell polled `location_id=<other org's location>` against the new one.
  // Measured on a CSN load whose org list is just `7:Casa de Pedra` — a
  // `404 /api/alerts/queue/?location_id=9`, then a 200 on 7 once the list caught
  // up.
  //
  // The queue call is the cheap half. `/api/alerts/live/subscribe/` 404s the same
  // way ("Location was not found.") and, per the settle note above, a token for
  // the wrong location replaces the auth session out from under the live
  // listener — the channel falls back to a 20s poll, which is the difference
  // between an alert arriving instantly and arriving a poll later.
  //
  // Clearing rather than recomputing: the correct location is whatever the new
  // org resolves to, and the settle timer below establishes it on the next tick.
  const prevOrganizationRef = useRef(organizationId);
  useEffect(() => {
    if (prevOrganizationRef.current === organizationId) return;
    prevOrganizationRef.current = organizationId;
    setSettledLocationId(null);
  }, [organizationId]);

  useEffect(() => {
    if (resolvedLocationId === settledLocationId) return;
    const id = setTimeout(() => {
      // Only settle on a value that is STILL the resolution — a change during
      // the wait restarts it via this effect's own dependency.
      if (pendingRef.current === resolvedLocationId) setSettledLocationId(resolvedLocationId);
    }, SCOPE_SETTLE_MS);
    return () => clearTimeout(id);
  }, [resolvedLocationId, settledLocationId]);

  return useMemo(
    () => ({
      organizationId,
      locationId: settledLocationId,
      scopeKey: settledLocationId != null ? `loc:${settledLocationId}` : null,
    }),
    [organizationId, settledLocationId],
  );
};
